// src/ui/CellPickerModal.ts
//
// Board-spatial cell picker overlay. Renders BoardLayout.grid 2D arrangement
// (NOT the flat cells[] array) so consumable targeting matches the visual board.
// Replaces RoundScene's deprecated _showCellPickerInternal.
//
// Phase 20 (UI-20): extract cell picker into a reusable Container class.
// Grid is traversed row-by-row, col-by-col from BoardLayout.grid (5×14).
// Aliased cells (zero spans rows 1-3, dozens span 4 cols, even-money spans 2 cols)
// are deduplicated via Set<BoardCell> reference equality and rendered once at
// their first appearance position.
//
// Pure helpers (dedupGridCells, resolveCellIndex, cellSpan) are exported so
// they can be unit-tested per CLAUDE.md (no DOM/Phaser tests).

import Phaser from 'phaser';
import type { BoardLayout, BoardCell, BetType, SlotNumber } from '../state/types';
import { RED_NUMBERS } from '../systems/wheel';
import { addCrispText } from './textHelper';
// Phase 21 (WR-02): shared pure helper for consistent outside-sub cell labels.
import { cellLabelForSub } from './betLabel';

// ─── Pure helpers (exported for unit tests) ───────────────────────────────────

/**
 * Pure helper: returns one entry per unique BoardCell, at its first grid
 * appearance (row-major order). Aliased cells (zero, dozens, even-money) that
 * appear at multiple grid positions are included only once.
 */
export function dedupGridCells(
  layout: BoardLayout,
): Array<{ cell: BoardCell; row: number; col: number }> {
  // Dedup by (cell.row, cell.col) instead of object identity so that stale
  // grid aliases (whose object refs break after mutateBoardCell) don't appear
  // as duplicate phantom cells in the picker.
  const seen = new Set<string>();
  const out: Array<{ cell: BoardCell; row: number; col: number }> = [];
  for (let row = 0; row < layout.dims.rows; row++) {
    for (let col = 0; col < layout.dims.cols; col++) {
      const cell = layout.grid[row]?.[col];
      if (!cell) continue;
      const key = `${cell.row},${cell.col}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ cell, row, col });
    }
  }
  return out;
}

/**
 * Pure helper: resolves a BoardCell to its flat-array index in layout.cells[].
 * Returns -1 if the cell is not in the layout (defensive — should not occur
 * when called with cells sourced from the same layout).
 */
export function resolveCellIndex(layout: BoardLayout, cell: BoardCell): number {
  // Use position-based lookup instead of indexOf: after mutateBoardCell the
  // cloned cell in cells[] is a different object from the one in grid[][], so
  // indexOf returns -1 for any cell sourced from the grid (e.g. stale aliases).
  return layout.cells.findIndex((c) => c.row === cell.row && c.col === cell.col);
}

/**
 * Pure helper: returns the span multipliers for a cell, mirroring Table.ts draw() rules.
 *
 * - col===0 → { wCols: 1, hRows: 3 }  (zero spans all 3 number rows)
 * - row===0 → { wCols: 1, hRows: 1 }  (Phase 21: outside-sub cells are single-column wide)
 * - row===4 (dozen-sub) → { wCols: 1, hRows: 1 }  (each dozen-sub occupies 1 grid column)
 * - else               → { wCols: 1, hRows: 1 }  (number cells + column bets)
 */
export function cellSpan(
  cell: BoardCell,
  _dims: { rows: number; cols: number },
): { wCols: number; hRows: number } {
  if (cell.col === 0) return { wCols: 1, hRows: 3 }; // zero
  // dozen-sub cells at row 4 each occupy exactly 1 grid column (sub2 4th-col alias deduplicated)
  return { wCols: 1, hRows: 1 }; // numbers, column bets, outside-sub (Phase 21), dozen-sub
}

// ─── Module-private helpers (mirrors Table.ts verbatim) ───────────────────────

function cellFillColor(bet: BetType): number {
  if (bet.kind === 'straight') {
    if (bet.number === 0) return 0x2a8a3a;
    return RED_NUMBERS.has(bet.number as SlotNumber) ? 0xb00020 : 0x111111;
  }
  if (bet.kind === 'red') return 0xb00020;
  if (bet.kind === 'black') return 0x111111;
  // Phase 21: outside-sub cells use parent-kind color
  if (bet.kind === 'outside-sub') {
    if (bet.parentKind === 'red') return 0xb00020;
    if (bet.parentKind === 'black') return 0x111111;
  }
  return 0x064020;
}

function cellLabel(bet: BetType): string {
  if (bet.kind === 'straight') return String(bet.number);
  if (bet.kind === 'red') return '♦';
  if (bet.kind === 'black') return '♠';
  if (bet.kind === 'even') return 'EVEN';
  if (bet.kind === 'odd') return 'ODD';
  if (bet.kind === 'low') return '1-18';
  if (bet.kind === 'high') return '19-36';
  if (bet.kind === 'dozen') return `D${bet.which}`;
  if (bet.kind === 'column') return `C${bet.which}`;
  // Phase 21: outside-sub cells use the same compact label as Table.ts (WR-02 fix).
  // Previously used min-max range which was misleading for sparse sets like red-L "1-18".
  if (bet.kind === 'outside-sub') return cellLabelForSub(bet);
  // Dozen subcells: D1a, D1b, D1c, D2a, etc.
  if (bet.kind === 'dozen-sub') {
    const suffix = ['a', 'b', 'c'][bet.subIdx] ?? 'a';
    return `D${bet.parentWhich}${suffix}`;
  }
  return '';
}

// ─── CellPickerModal ──────────────────────────────────────────────────────────

/**
 * Board-spatial cell picker overlay (Phaser Container, depth 50).
 *
 * Renders BoardLayout.grid in a 2D arrangement matching the real roulette board
 * so players can visually orient toward the cell they want to target.
 *
 * Usage:
 *   const picker = new CellPickerModal(scene, layout);
 *   picker.open('Select a cell', (cellIdx) => { ... });
 *
 * Two-step (CR-02 exclusion):
 *   picker.open('Select 1st cell', (firstIdx) => {
 *     const picker2 = new CellPickerModal(scene, layout);
 *     picker2.open('Select 2nd cell', (secondIdx) => { ... }, firstIdx);
 *   });
 */
export class CellPickerModal {
  private container: Phaser.GameObjects.Container | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly layout: BoardLayout,
  ) {}

  /**
   * Open the picker overlay. callback fires with the flat cells[] index of the
   * tapped cell, then the overlay is destroyed automatically.
   *
   * @param prompt       - Title text shown above the grid
   * @param callback     - Receives the flat-array index of the tapped cell
   * @param excludeIdx   - CR-02 exclusion: hide this cell index (two-step flow)
   * @param allowedKinds - If provided, only cells whose bet.kind is in this list
   *                       are interactive; others are shown dimmed (alpha 0.25).
   * @param subtitle     - Optional description shown below the title.
   * @param imageInfo    - Optional consumable sprite shown large at the bottom.
   */
  public open(
    prompt: string,
    callback: (cellIdx: number) => void,
    excludeIdx?: number,
    allowedKinds?: string[],
    subtitle?: string,
    imageInfo?: { key: string; frame?: number },
  ): void {
    this.destroy(); // singleton guard: close any previously-open overlay

    const c = this.scene.add.container(0, 0).setDepth(50);
    this.container = c;

    // Backdrop — absorbs taps outside cells (no-op handler prevents tap-through).
    const backdrop = this.scene.add
      .rectangle(187, 333, 375, 667, 0x000000, 0.7)
      .setOrigin(0.5, 0.5)
      .setInteractive();
    backdrop.on('pointerdown', () => {
      // intentional no-op: prevents clicks from reaching underlying scene objects
    });
    c.add(backdrop);

    // Title
    const title = addCrispText(this.scene, 187, 30, prompt, {
      color: '#d4af37',
      fontSize: '13px',
    }).setOrigin(0.5, 0.5);
    c.add(title);

    if (subtitle) {
      const sub = addCrispText(this.scene, 187, 48, subtitle, {
        color: '#aaaaaa',
        fontSize: '10px',
        wordWrap: { width: 340 },
        align: 'center',
      }).setOrigin(0.5, 0.5);
      c.add(sub);
    }

    // Grid layout dimensions (touch-safe at colW=24, rowH=26 → 336×130 total)
    // 14 cols × 24px = 336px; 5 rows × 26px = 130px — fits 375×667 with margins.
    const colW = 24;
    const rowH = 26;
    const gridW = this.layout.dims.cols * colW; // 14 × 24 = 336
    const startX = (375 - gridW) / 2; // ~19.5 — centered in 375px canvas
    const startY = 70; // below title + padding

    // Render deduplicated cells at their board-spatial grid positions
    for (const { cell, row, col } of dedupGridCells(this.layout)) {
      const idx = resolveCellIndex(this.layout, cell);
      if (idx === excludeIdx) continue; // CR-02 exclusion

      const span = cellSpan(cell, this.layout.dims);
      const cellW = colW * span.wCols;
      const cellH = rowH * span.hRows;
      const cx = startX + col * colW;
      const cy = startY + row * rowH;

      const fill = cellFillColor(cell.bet);
      const rect = this.scene.add
        .rectangle(cx, cy, cellW, cellH, fill)
        .setOrigin(0, 0)
        .setStrokeStyle(0.5, 0xffffff);
      c.add(rect);

      // Font size mirrors Table.ts: zero = 10px, outside rows (≥3) = 8px, else 9px
      const fontSize = cell.col === 0 ? '10px' : cell.row >= 3 ? '8px' : '9px';
      const lbl = addCrispText(this.scene, cx + cellW / 2, cy + cellH / 2, cellLabel(cell.bet), {
        color: '#ffffff',
        fontSize,
      }).setOrigin(0.5, 0.5);
      c.add(lbl);

      // allowedKinds: dim and disable cells not in the allowed set
      const allowed = !allowedKinds || allowedKinds.includes(cell.bet.kind);
      if (allowed) {
        rect.setInteractive({ useHandCursor: true });
        // Tap handler: destroy overlay then fire callback with flat-array index
        rect.on('pointerdown', () => {
          this.destroy();
          callback(idx);
        });
      } else {
        rect.setAlpha(0.25); // dim disallowed cells
      }
    }

    // Consumable image preview at bottom
    if (imageInfo && this.scene.textures.exists(imageInfo.key)) {
      const img = this.scene.add
        .image(187, 580, imageInfo.key, imageInfo.frame)
        .setDisplaySize(100, 100)
        .setOrigin(0.5, 0.5)
        .setAlpha(0.9);
      c.add(img);
    }

    // Cancel button — dismisses without firing callback (no partial consumable use)
    const cancelBtn = this.scene.add
      .rectangle(187, 645, 120, 28, 0x333333)
      .setInteractive({ useHandCursor: true });
    const cancelLabel = addCrispText(this.scene, 187, 645, 'Cancel', {
      color: '#ffffff',
      fontSize: '11px',
    }).setOrigin(0.5, 0.5);
    c.add(cancelBtn);
    c.add(cancelLabel);
    cancelBtn.on('pointerdown', () => {
      this.destroy();
    });
  }

  /**
   * Destroy the overlay container if currently open. Idempotent — safe to call
   * multiple times (T-20-02 mitigation).
   */
  public destroy(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  /** Returns true if the modal is currently open. */
  public isOpen(): boolean {
    return this.container !== null;
  }
}
